import React, {useState} from "react";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import {makeStyles} from '@material-ui/core/styles';
import Paper from "@material-ui/core/Paper";
import Switch from "@material-ui/core/Switch";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import RagnarokHub from "../context/RagnarokHub";
import CloudIcon from '@material-ui/icons/Cloud';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import copy from 'clipboard-copy';
import LinkIcon from '@material-ui/icons/Link';
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Checkbox from "@material-ui/core/Checkbox";
import {Link} from "@material-ui/core";
import GrafanaIcon from "../media/grafana_icon.svg";
import {useAuth0} from "../auth/sso";
import Loading from "./Loading";
import {useNavBarContext} from "./NavBar";
import HelpIcon from '@material-ui/icons/Help';

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        overflow: 'hidden',
        padding: theme.spacing(2, 3),
    },
    paper: {
        maxWidth: 400,
        marginBottom: `${theme.spacing(2)}px`,
        padding: theme.spacing(2),
    },
}));

function renderLaunchButton(classes, launched, handleHubUpdate) {
    const title = !launched ? "LAUNCH" : "STOP";
    const color = !launched ? "primary" : "secondary";

    return (
        <Button
            variant="contained"
            color={color}
            className={classes.button}
            onClick={() => handleHubUpdate(!launched)}
        >
            {title}
        </Button>
    );
}

function handleIntParameterChange(event, thingId, key, handleHubThingActuatorConfigurationChange,
                                  handleAddToValidationActuatorMap, handleRemoveFromValidationActuatorMap, min, max) {
    event.preventDefault();

    if (!event.target.value) {
        handleAddToValidationActuatorMap(thingId, key);
        handleHubThingActuatorConfigurationChange(thingId, key, '');
        return;
    }

    let value = parseInt(event.target.value);
    if (value < 0) value = 0;

    if (min && max && (value < min || value > max)) {
        handleAddToValidationActuatorMap(thingId, key);
        return;
    }

    handleHubThingActuatorConfigurationChange(thingId, key, value);
    handleRemoveFromValidationActuatorMap(thingId, key);
}

function handleFloatParameterChange(event, thingId, key, handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap, handleRemoveFromValidationActuatorMap, min, max) {
    event.preventDefault();

    if (!event.target.value) {
        handleAddToValidationActuatorMap(thingId, key);
        handleHubThingActuatorConfigurationChange(thingId, key, '');
        return;
    }

    let value = parseFloat(event.target.value);

    if (min && max && (value < min || value > max)) {
        handleHubThingActuatorConfigurationChange(thingId, key, value);
        handleAddToValidationActuatorMap(thingId, key);
        return;
    }

    handleHubThingActuatorConfigurationChange(thingId, key, value);
    handleRemoveFromValidationActuatorMap(thingId, key);
}

function handleOnBluer(event, thingId, validationMap, handleHubThingActuatorConfigurationUpdate) {
    event.preventDefault();

    if (!hasValidationErrors(validationMap))
        handleHubThingActuatorConfigurationUpdate(thingId);
}

function hasValidationErrors(validationMap) {
    return Object.keys(validationMap).filter(key => validationMap[key]).length
}

function RenderActuatorConfiguration({
                                         thingId,
                                         validationMap,
                                         manufacturer,
                                         configuration,
                                         handleHubThingActuatorConfigurationChange,
                                         handleHubThingActuatorConfigurationUpdate,
                                         handleAddToValidationActuatorMap,
                                         handleRemoveFromValidationActuatorMap,
                                         handleHubThingExecuteAction
                                     }) {
    const [data, setData] = useState({});

    switch (manufacturer) {
        case "SORENSEN_XG": {
            const {address, current_set_point, voltage_set_point, main_output_control} = configuration;

            return (
                <Grid container spacing={2} direction={"column"}>
                    <Grid item>
                        <FormControl>
                            <FormGroup row>
                                <FormControlLabel
                                    value="main-output-control"
                                    control={
                                        <Switch
                                            checked={main_output_control === "ON"}
                                            onChange={(event) => {
                                                let value = event.target.checked ? "ON" : "OFF";

                                                handleHubThingActuatorConfigurationChange(
                                                    thingId,
                                                    "main_output_control",
                                                    value
                                                );

                                                if (!hasValidationErrors(validationMap))
                                                    handleHubThingActuatorConfigurationUpdate(thingId);
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography style={{fontSize: "0.9rem"}}>
                                            Main Output Control
                                        </Typography>
                                    }
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <TextField
                            id="address"
                            error={validationMap["address"]}
                            type={"number"}
                            label="Address"
                            value={address}
                            inputProps={{
                                step: 1,
                                style: {
                                    width: 150,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleIntParameterChange(
                                    event,
                                    thingId,
                                    "address",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            id="current-set-point"
                            error={validationMap["current_set_point"]}
                            type={"number"}
                            label="Current Set Point"
                            value={current_set_point}
                            inputProps={{
                                step: 0.1,
                                style: {
                                    width: 150,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "current_set_point",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            id="voltage-set-point"
                            error={validationMap["voltage_set_point"]}
                            type={"number"}
                            label="Voltage Set Point"
                            value={voltage_set_point}
                            inputProps={{
                                min: 0,
                                step: 0.1,
                                style: {
                                    width: 150,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "voltage_set_point",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                </Grid>
            );
        }

        case "SPELLMAN_ICA": {
            const {
                heater_grid_enables,
                arc_fault_program,
                mean_cathode_over_current,
                grid_pulsed_voltage,
                cathode_voltage,
                heater_voltage
            } = configuration;

            const buttonWidth = 200;
            const inputWidth = 300;

            return (
                <Grid container spacing={2} direction={"column"}>
                    <Grid item>
                        <Button size="small"
                                variant="contained"
                                style={{width: buttonWidth}}
                                onClick={() => handleHubThingExecuteAction(thingId, ["RESET_ARC_COUNTER"])}
                        >
                            Reset Arc Counter
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button size="small"
                                variant="contained"
                                style={{width: buttonWidth}}
                                onClick={() => handleHubThingExecuteAction(thingId, ["RESET_FAULTS"])}
                        >
                            Reset Faults
                        </Button>
                    </Grid>
                    <Grid item>
                        <FormControl>
                            <FormGroup row>
                                <FormControlLabel
                                    value="heater_grid_enables"
                                    control={
                                        <Switch
                                            checked={heater_grid_enables}
                                            onChange={(event) => {
                                                let value = event.target.checked;

                                                handleHubThingActuatorConfigurationChange(
                                                    thingId,
                                                    "heater_grid_enables",
                                                    value
                                                );

                                                if (!hasValidationErrors(validationMap))
                                                    handleHubThingActuatorConfigurationUpdate(thingId);
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography style={{fontSize: "0.9rem"}}>
                                            Heater Grid Enables
                                        </Typography>
                                    }
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <TextField
                            id="arc_fault_program"
                            error={validationMap["arc_fault_program"]}
                            type={"number"}
                            label="Arc Fault Program (1-250)"
                            value={arc_fault_program}
                            inputProps={{
                                step: 1,
                                style: {
                                    width: inputWidth,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleIntParameterChange(
                                    event,
                                    thingId,
                                    "arc_fault_program",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap,
                                    1, 250)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            id="mean_cathode_over_current"
                            error={validationMap["mean_cathode_over_current"]}
                            type={"number"}
                            label="Mean Cathode Over Current mA (0-10)"
                            value={mean_cathode_over_current}
                            inputProps={{
                                step: 0.1,
                                style: {
                                    width: inputWidth,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "mean_cathode_over_current",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap,
                                    0, 10)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            id="grid_pulsed_voltage"
                            error={validationMap["grid_pulsed_voltage"]}
                            type={"number"}
                            label="Grid Pulsed Voltage V (0-15)"
                            value={grid_pulsed_voltage}
                            inputProps={{
                                min: 0,
                                step: 0.1,
                                style: {
                                    width: inputWidth,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "grid_pulsed_voltage",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap,
                                    0, 15)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            id="cathode_voltage"
                            error={validationMap["cathode_voltage"]}
                            type={"number"}
                            label="Cathode Voltage kV (0-15)"
                            value={cathode_voltage}
                            inputProps={{
                                min: 0,
                                step: 0.1,
                                style: {
                                    width: inputWidth,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "cathode_voltage",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap,
                                    0, 15)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            id="heater_voltage"
                            error={validationMap["heater_voltage"]}
                            type={"number"}
                            label="Heater Voltage V (0-6.5)"
                            value={heater_voltage}
                            inputProps={{
                                min: 0,
                                step: 0.1,
                                style: {
                                    width: inputWidth,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "heater_voltage",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                </Grid>
            );
        }

        case "PEAK_COM_ARU2856C": {
            const {
                power_level,
                override_rf_inhibit_status,
                frequency,
                trim
            } = configuration;

            const inputWidth = 300;

            const stepSize = [
                {
                    "title": "10 MHz",
                    "value": 10000
                },
                {
                    "title": "1 MHz",
                    "value": 1000
                },
                {
                    "title": "100 kHz",
                    "value": 100
                },
                {
                    "title": "10 kHz",
                    "value": 10
                },
                {
                    "title": "1 kHz",
                    "value": 1
                }
            ];

            const local = {
                stepSize: data["stepSize"] || 1
            };

            return (
                <Grid container spacing={2} direction={"column"}>
                    <Grid item>
                        <FormControl>
                            <FormGroup row>
                                <FormControlLabel
                                    value="override_rf_inhibit_status"
                                    control={
                                        <Switch
                                            checked={override_rf_inhibit_status === "ENABLED"}
                                            onChange={(event) => {
                                                const value = event.target.checked ? "ENABLED" : "INHIBITED";

                                                handleHubThingActuatorConfigurationChange(
                                                    thingId,
                                                    "override_rf_inhibit_status",
                                                    value
                                                );

                                                if (!hasValidationErrors(validationMap))
                                                    handleHubThingActuatorConfigurationUpdate(thingId);
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography style={{fontSize: "0.9rem"}}>
                                            Override RF Inhibit Status
                                        </Typography>
                                    }
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item container spacing={2}>
                        <Grid item>
                            <Typography style={{fontSize: "0.9rem"}}>Frequency Step Size</Typography>
                            <Select
                                value={local.stepSize}
                                onChange={(event) => {
                                    event.preventDefault();
                                    setData({stepSize: event.target.value});
                                }}
                                inputProps={{
                                    id: 'frequency-step-size',
                                    name: 'frequency-step-size'
                                }}
                                style={{width: "100px"}}
                            >
                                {
                                    stepSize.map((data, index) => (
                                        <MenuItem key={`step_size_${index}`} value={data.value}>{data.title}</MenuItem>
                                    ))
                                }
                            </Select>
                        </Grid>
                        <Grid item>
                            <TextField
                                id="frequency"
                                error={validationMap["frequency"]}
                                type={"number"}
                                label="Frequency (2840000-2870000)"
                                value={frequency}
                                inputProps={{
                                    step: local.stepSize,
                                    style: {
                                        width: inputWidth,
                                        textAlign: "center"
                                    }
                                }}
                                onChange={
                                    (event) => handleFloatParameterChange(
                                        event,
                                        thingId,
                                        "frequency",
                                        handleHubThingActuatorConfigurationChange,
                                        handleAddToValidationActuatorMap,
                                        handleRemoveFromValidationActuatorMap,
                                        2840000, 2870000)
                                }
                                onBlur={(event) => handleOnBluer(event, thingId,
                                    validationMap, handleHubThingActuatorConfigurationUpdate)}
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <TextField
                            id="power_level"
                            error={validationMap["power_level"]}
                            type={"number"}
                            label="Power Level (-300-300)"
                            value={power_level}
                            inputProps={{
                                step: 1,
                                style: {
                                    width: inputWidth,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "power_level",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap,
                                    -300, 300)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            id="trim"
                            error={validationMap["trim"]}
                            type={"number"}
                            label="Trim (-50-50)"
                            value={trim}
                            inputProps={{
                                step: 1,
                                style: {
                                    width: inputWidth,
                                    textAlign: "center"
                                }
                            }}
                            onChange={
                                (event) => handleFloatParameterChange(
                                    event,
                                    thingId,
                                    "trim",
                                    handleHubThingActuatorConfigurationChange,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap,
                                    -50, 50)
                            }
                            onBlur={(event) => handleOnBluer(event, thingId,
                                validationMap, handleHubThingActuatorConfigurationUpdate)}
                        />
                    </Grid>
                </Grid>
            );
        }

        default:
            return (
                <Typography>---</Typography>
            );
    }
}

function tcpHostComponent(thingId,
                          host,
                          validationMap,
                          handleAddToValidationActuatorMap,
                          handleRemoveFromValidationActuatorMap,
                          handleHubThingConnectionConfigurationChange,
                          handleHubThingConnectionConfigurationUpdate) {
    const error = validationMap["tcp_host"];

    return (
        <TextField
            id="connection-conf-tcp-host"
            error={error}
            label="Host"
            value={host || ''}
            inputProps={{
                min: 0,
                step: 0.1,
                style: {
                    width: 150,
                    textAlign: "center"
                }
            }}
            onChange={(event) => {
                event.preventDefault();

                const value = event.target.value;

                handleHubThingConnectionConfigurationChange(thingId, {host: value});

                if (/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(value)) {
                    handleRemoveFromValidationActuatorMap(thingId, "tcp_host");
                    return;
                }

                handleAddToValidationActuatorMap(thingId, "tcp_host");
            }}
            onBlur={(event) => {
                event.preventDefault();

                if (Object.keys(validationMap).map(key => validationMap[key]).filter(error => error === true).length) return;

                handleHubThingConnectionConfigurationUpdate(thingId);
            }}
        />
    )
}

function tcpPortComponent(thingId,
                          port,
                          validationMap,
                          handleAddToValidationActuatorMap,
                          handleRemoveFromValidationActuatorMap,
                          handleHubThingConnectionConfigurationChange,
                          handleHubThingConnectionConfigurationUpdate) {
    const error = validationMap["tcp_port"];

    return (
        <TextField
            id="connection-conf-tcp-port"
            error={error}
            type={"number"}
            label="Port"
            value={port || ''}
            inputProps={{
                min: 0,
                step: 1,
                style: {
                    width: 80,
                    textAlign: "center"
                }
            }}
            onChange={(event) => {
                event.preventDefault();

                const value = event.target.value;

                if (!value) {
                    handleHubThingConnectionConfigurationChange(thingId, {port: ''});
                    handleAddToValidationActuatorMap(thingId, "tcp_port");
                    return;
                }

                handleHubThingConnectionConfigurationChange(thingId, {port: parseInt(value)});
                handleRemoveFromValidationActuatorMap(thingId, "tcp_port");
            }}
            onBlur={(event) => {
                event.preventDefault();

                if (Object.keys(validationMap).map(key => validationMap[key]).filter(error => error === true).length) return;

                handleHubThingConnectionConfigurationUpdate(thingId);
            }}
        />
    );
}

function connConfTextFieldComponent(thingId,
                                    name,
                                    type,
                                    label,
                                    value,
                                    width,
                                    validationMap,
                                    handleAddToValidationActuatorMap,
                                    handleRemoveFromValidationActuatorMap,
                                    handleHubThingConnectionConfigurationChange,
                                    handleHubThingConnectionConfigurationUpdate) {
    const error = validationMap[name];

    return (
        <TextField
            id={`connection-conf-${name}`}
            error={error}
            type={type}
            label={label}
            value={(value === null || value === undefined) ? '' : value}
            inputProps={{
                min: 0,
                step: 1,
                style: {
                    width: width,
                    textAlign: "center"
                }
            }}
            onChange={(event) => {
                event.preventDefault();

                const value = event.target.value;

                if (!value) {
                    handleHubThingConnectionConfigurationChange(thingId, '');
                    handleAddToValidationActuatorMap(thingId, name);
                    return;
                }

                handleHubThingConnectionConfigurationChange(thingId, value);
                handleRemoveFromValidationActuatorMap(thingId, name);
            }}
            onBlur={(event) => {
                event.preventDefault();

                if (Object
                    .keys(validationMap)
                    .map(key => validationMap[key])
                    .filter(error => error === true).length) return;

                handleHubThingConnectionConfigurationUpdate(thingId);
            }}
        />
    );
}

function serialComponent(thingId,
                         configuration,
                         validationMap,
                         handleAddToValidationActuatorMap,
                         handleRemoveFromValidationActuatorMap,
                         handleHubThingConnectionConfigurationChange,
                         handleHubThingConnectionConfigurationUpdate) {
    const {port, baud_rate, data_bits, stop_bits, parity} = configuration;

    const width = 200;

    return (
        <Grid item xs container direction="column">
            <Grid item xs>
                {
                    connConfTextFieldComponent(
                        thingId,
                        "serial-port",
                        "text",
                        "Port",
                        port,
                        width,
                        validationMap,
                        handleAddToValidationActuatorMap,
                        handleRemoveFromValidationActuatorMap,
                        (thingId, value) =>
                            handleHubThingConnectionConfigurationChange(thingId, {port: value}),
                        handleHubThingConnectionConfigurationUpdate
                    )
                }
            </Grid>
            <Grid item xs>
                {
                    connConfTextFieldComponent(
                        thingId,
                        "serial-baud-rate",
                        "number",
                        "Baud rate",
                        baud_rate,
                        width,
                        validationMap,
                        handleAddToValidationActuatorMap,
                        handleRemoveFromValidationActuatorMap,
                        (thingId, value) =>
                            handleHubThingConnectionConfigurationChange(thingId, {baud_rate: parseInt(value)}),
                        handleHubThingConnectionConfigurationUpdate
                    )
                }
            </Grid>
            <Grid item xs>
                {
                    connConfTextFieldComponent(
                        thingId,
                        "serial-data-bits",
                        "number",
                        "Data bits",
                        data_bits,
                        width,
                        validationMap,
                        handleAddToValidationActuatorMap,
                        handleRemoveFromValidationActuatorMap,
                        (thingId, value) =>
                            handleHubThingConnectionConfigurationChange(thingId, {data_bits: parseInt(value)}),
                        handleHubThingConnectionConfigurationUpdate
                    )
                }
            </Grid>
            <Grid item xs>
                {
                    connConfTextFieldComponent(
                        thingId,
                        "serial-stop-bits",
                        "number",
                        "Stop bits",
                        stop_bits,
                        width,
                        validationMap,
                        handleAddToValidationActuatorMap,
                        handleRemoveFromValidationActuatorMap,
                        (thingId, value) =>
                            handleHubThingConnectionConfigurationChange(thingId, {stop_bits: parseInt(value)}),
                        handleHubThingConnectionConfigurationUpdate
                    )
                }
            </Grid>
            <Grid item xs>
                {
                    connConfTextFieldComponent(
                        thingId,
                        "serial-parity",
                        "number",
                        "Parity",
                        parity,
                        width,
                        validationMap,
                        handleAddToValidationActuatorMap,
                        handleRemoveFromValidationActuatorMap,
                        (thingId, value) =>
                            handleHubThingConnectionConfigurationChange(thingId, {parity: parseInt(value)}),
                        handleHubThingConnectionConfigurationUpdate
                    )
                }
            </Grid>
        </Grid>
    );
}

function renderConnectionConfiguration(thingId,
                                       protocol,
                                       configuration,
                                       validationMap,
                                       handleAddToValidationActuatorMap,
                                       handleRemoveFromValidationActuatorMap,
                                       handleHubThingConnectionConfigurationChange,
                                       handleHubThingConnectionConfigurationUpdate) {
    switch (protocol) {
        case "TCP":
        case "TCP_RS_232":
            return (
                <Grid item xs container direction="row">
                    <Grid item xs>
                        {
                            tcpHostComponent(
                                thingId,
                                configuration.host,
                                validationMap,
                                handleAddToValidationActuatorMap,
                                handleRemoveFromValidationActuatorMap,
                                handleHubThingConnectionConfigurationChange,
                                handleHubThingConnectionConfigurationUpdate
                            )
                        }
                    </Grid>
                    <Grid item xs>
                        {
                            tcpPortComponent(
                                thingId,
                                configuration.port,
                                validationMap,
                                handleAddToValidationActuatorMap,
                                handleRemoveFromValidationActuatorMap,
                                handleHubThingConnectionConfigurationChange,
                                handleHubThingConnectionConfigurationUpdate
                            )
                        }
                    </Grid>
                </Grid>
            );

        case "UDP":
        case "MODBUS_TCP":
            return (
                <Grid item xs container direction="row">
                    <Grid item xs>
                        {
                            tcpHostComponent(
                                thingId,
                                configuration.host,
                                validationMap,
                                handleAddToValidationActuatorMap,
                                handleRemoveFromValidationActuatorMap,
                                handleHubThingConnectionConfigurationChange,
                                handleHubThingConnectionConfigurationUpdate
                            )
                        }
                    </Grid>
                </Grid>
            );

        case "SERIAL_PORT":
            return serialComponent(
                thingId,
                configuration,
                validationMap,
                handleAddToValidationActuatorMap,
                handleRemoveFromValidationActuatorMap,
                handleHubThingConnectionConfigurationChange,
                handleHubThingConnectionConfigurationUpdate
            );

        default:
            return null;
    }
}

function RenderControlPanel({
                                id,
                                manufacturer,
                                actuator_configuration,
                                validationMap,
                                handleHubThingActuatorConfigurationChange,
                                handleHubThingActuatorConfigurationUpdate,
                                handleAddToValidationActuatorMap,
                                handleRemoveFromValidationActuatorMap,
                                handleHubThingExecuteAction
                            }) {
    if (["SORENSEN_XG", "SPELLMAN_ICA", "PEAK_COM_ARU2856C"].indexOf(manufacturer) === -1) return null;

    return (
        <Grid item>
            <Typography variant="subtitle2">Control Panel</Typography>
            <Grid item xs container direction="column">
                <RenderActuatorConfiguration thingId={id}
                                             validationMap={validationMap}
                                             manufacturer={manufacturer}
                                             configuration={actuator_configuration}
                                             handleHubThingActuatorConfigurationChange={handleHubThingActuatorConfigurationChange}
                                             handleHubThingActuatorConfigurationUpdate={handleHubThingActuatorConfigurationUpdate}
                                             handleAddToValidationActuatorMap={handleAddToValidationActuatorMap}
                                             handleRemoveFromValidationActuatorMap={handleRemoveFromValidationActuatorMap}
                                             handleHubThingExecuteAction={handleHubThingExecuteAction}
                />
            </Grid>
        </Grid>
    );
}

function getPowerMetric(configuration, channel, window) {
    if (!configuration ||
        !configuration[channel] ||
        !configuration[channel]["windows"] ||
        !configuration[channel]["windows"][window] ||
        !configuration[channel]["windows"][window]["power"]) return {enabled: false, method: undefined};

    return {enabled: true, method: configuration[channel]["windows"][window]["power"]};
}

function renderAgilentEPMPWindowMetricSelector(thingId, channel, window, powerMetric,
                                               handleHubThingCollectionConfigurationChange,
                                               handleHubThingCollectionConfigurationUpdate) {
    const options = ["FETCh", "READ", "MEASure"];
    const method = powerMetric.method || options[0];

    return (
        <FormGroup row>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={powerMetric.enabled}
                        onChange={event => {
                            const power = event.target.checked ? method : undefined;

                            handleHubThingCollectionConfigurationChange(thingId, {
                                [window]: {power}
                            });

                            handleHubThingCollectionConfigurationUpdate(thingId);
                        }}
                        value={`channel-${channel}-window-${window}-power`}
                        color="primary"
                    />
                }
                label={`${window} Window [POWER]`}
            />
            <FormControl disabled={!powerMetric.enabled}>
                <Select
                    value={method}

                    onChange={(event) => {
                        event.preventDefault();
                        const power = event.target.value;

                        handleHubThingCollectionConfigurationChange(thingId, {
                            [window]: {power}
                        });

                        handleHubThingCollectionConfigurationUpdate(thingId);
                    }}
                    inputProps={{
                        id: 'collection-interval',
                        name: 'collection-interval'
                    }}
                    style={{width: "100px"}}
                >
                    {
                        options.map((interval, index) => (
                            <MenuItem key={`channel-${channel}-window-${window}-power-${index}`}
                                      value={interval}>{interval}</MenuItem>
                        ))
                    }
                </Select>
            </FormControl>
        </FormGroup>
    )
}

function renderAgilentEPMPPowerMetricSelector(thingId,
                                              channel,
                                              configuration,
                                              handleHubThingCollectionConfigurationChange,
                                              handleHubThingCollectionConfigurationUpdate) {
    const upper = getPowerMetric(configuration, channel, "UPPER");
    const lower = getPowerMetric(configuration, channel, "LOWER");

    const metricSelectorHandler = (thingId, value) => {
        if (configuration != null) {
            if (configuration[channel] != null) {
                const windows = configuration[channel]["windows"] != null
                    ? Object.assign(configuration[channel]["windows"], value)
                    : value;

                const channels = Object.assign(configuration, {[channel]: {windows: windows}});
                handleHubThingCollectionConfigurationChange(thingId, {channels});

                return;
            }

            const channels = Object.assign(configuration, {[channel]: {windows: value}});
            handleHubThingCollectionConfigurationChange(thingId, {channels});

            return;
        }

        const conf = {channels: {[channel]: {windows: value}}};
        handleHubThingCollectionConfigurationChange(thingId, conf);
    };

    return (
        <Grid>
            <Typography variant="body2" color="textSecondary">
                Channel: {channel}
            </Typography>
            <FormControl>
                {
                    renderAgilentEPMPWindowMetricSelector(
                        thingId,
                        channel,
                        "UPPER",
                        upper,
                        metricSelectorHandler,
                        handleHubThingCollectionConfigurationUpdate)
                }
                {
                    renderAgilentEPMPWindowMetricSelector(
                        thingId,
                        channel,
                        "LOWER",
                        lower,
                        metricSelectorHandler,
                        handleHubThingCollectionConfigurationUpdate)
                }
            </FormControl>
        </Grid>
    );
}

function renderCollectionConfiguration(thingId,
                                       manufacturer,
                                       configuration,
                                       handleHubThingCollectionConfigurationChange,
                                       handleHubThingCollectionConfigurationUpdate) {
    switch (manufacturer) {
        case "AGILENT_EPM_P": {
            const {attenuation, channels} = configuration;

            return (
                <Grid container spacing={2} direction={"column"}>
                    <Grid item>
                        <TextField
                            id="address"
                            type={"number"}
                            label="Attenuation (offset)"
                            value={attenuation || ''}
                            inputProps={{
                                step: 1,
                                style: {
                                    width: 150,
                                    textAlign: "center"
                                }
                            }}
                            onChange={event => {
                                event.preventDefault();

                                if (!event.target.value) {
                                    handleHubThingCollectionConfigurationChange(thingId, {attenuation: ''});
                                    return;
                                }

                                const value = parseInt(event.target.value);

                                handleHubThingCollectionConfigurationChange(thingId, {attenuation: value});
                            }}
                            onBlur={event => {
                                event.preventDefault();

                                if (attenuation === '')
                                    handleHubThingCollectionConfigurationChange(thingId, {attenuation: 0});

                                handleHubThingCollectionConfigurationUpdate(thingId);
                            }}
                        />
                    </Grid>
                    {
                        renderAgilentEPMPPowerMetricSelector(
                            thingId,
                            "A",
                            channels,
                            handleHubThingCollectionConfigurationChange,
                            handleHubThingCollectionConfigurationUpdate)
                    }
                    {
                        renderAgilentEPMPPowerMetricSelector(
                            thingId,
                            "B",
                            channels,
                            handleHubThingCollectionConfigurationChange,
                            handleHubThingCollectionConfigurationUpdate)
                    }
                </Grid>
            );
        }

        default:
            return null;
    }
}

function renderThings(
    classes,
    things,
    actuatorValidationMap,
    handleHubThingEnableUpdate,
    handleHubThingCollectionIntervalUpdate,
    handleHubThingActuatorConfigurationChange,
    handleHubThingActuatorConfigurationUpdate,
    handleAddToValidationActuatorMap,
    handleRemoveFromValidationActuatorMap,
    handleHubThingConnectionConfigurationChange,
    handleHubThingConnectionConfigurationUpdate,
    handleHubThingCollectionConfigurationChange,
    handleHubThingCollectionConfigurationUpdate,
    handleHubThingExecuteAction) {
    if (!things.length) return null;

    return (
        <Grid item xs={10}>
            <Typography variant="subtitle1" gutterBottom>Things</Typography>
            {things.map((thing) => {
                    const {
                        id,
                        hubId,
                        enabled,
                        manufacturer,
                        collection_interval,
                        collection_configuration,
                        connection_protocol,
                        connection_configuration,
                        actuator_configuration
                    } = thing;

                    return (
                        <Paper className={classes.paper} key={`hub_${hubId}_thing_${id}`}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm container>
                                    <Grid item xs container direction="column" spacing={2}>
                                        <Grid item xs>
                                            <Typography gutterBottom variant="subtitle1">
                                                {manufacturer.replace(new RegExp('_', 'g'), " ")}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <InputLabel htmlFor="collection-interval">
                                                Collection Interval (sec)
                                            </InputLabel>
                                            <Select
                                                value={collection_interval / 1000}
                                                onChange={(event) => {
                                                    event.preventDefault();
                                                    const period = parseFloat(event.target.value) * 1000;
                                                    handleHubThingCollectionIntervalUpdate(id, period);
                                                }}
                                                inputProps={{
                                                    id: 'collection-interval',
                                                    name: 'collection-interval'
                                                }}
                                                style={{width: "100px"}}
                                            >
                                                {[
                                                    0.1, 0.2, 0.3, 0.4, 0.5,
                                                    1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
                                                    15, 20, 25, 30
                                                ].map((interval, index) => (
                                                    <MenuItem key={`thing_${id}_collection_interval_${index}`}
                                                              value={interval}>{interval}</MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle2">
                                                Connection [{connection_protocol}]
                                            </Typography>
                                            {
                                                renderConnectionConfiguration(
                                                    id,
                                                    connection_protocol,
                                                    connection_configuration,
                                                    actuatorValidationMap[id],
                                                    handleAddToValidationActuatorMap,
                                                    handleRemoveFromValidationActuatorMap,
                                                    handleHubThingConnectionConfigurationChange,
                                                    handleHubThingConnectionConfigurationUpdate
                                                )
                                            }
                                        </Grid>
                                        <RenderControlPanel id={id}
                                                            manufacturer={manufacturer}
                                                            actuator_configuration={actuator_configuration}
                                                            validationMap={actuatorValidationMap[id]}
                                                            handleHubThingActuatorConfigurationChange={handleHubThingActuatorConfigurationChange}
                                                            handleHubThingActuatorConfigurationUpdate={handleHubThingActuatorConfigurationUpdate}
                                                            handleAddToValidationActuatorMap={handleAddToValidationActuatorMap}
                                                            handleRemoveFromValidationActuatorMap={handleRemoveFromValidationActuatorMap}
                                                            handleHubThingExecuteAction={handleHubThingExecuteAction}
                                        />
                                        <Grid item>
                                            <Grid container spacing={1}>
                                                <Grid item>
                                                    <Typography variant="subtitle2">
                                                        Metrics
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Tooltip title={JSON.stringify(collection_configuration, null, 2)}>
                                                        <HelpIcon fontSize={"small"}/>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                            {
                                                renderCollectionConfiguration(
                                                    id,
                                                    manufacturer,
                                                    collection_configuration,
                                                    handleHubThingCollectionConfigurationChange,
                                                    handleHubThingCollectionConfigurationUpdate
                                                )
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <Switch
                                            checked={enabled}
                                            onChange={(event) => {
                                                handleHubThingEnableUpdate(id, event.target.checked);
                                            }}
                                            value="checkedB"
                                            color="primary"
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                }
            )}
        </Grid>
    );
}

function gatewayComponent(classes, tunnel) {
    const {id, connected, title, description} = tunnel;

    return (
        <Grid item xs={10}>
            <Typography variant="subtitle1" gutterBottom>Gateway</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <Typography variant="body2" color="textSecondary">Title:</Typography>
                        <Typography variant="subtitle1" color="textPrimary">{title}</Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <Tooltip title="Copy tunnel id to clipboard">
                            <IconButton aria-label="copy tunnel id" size="small"
                                        onClick={() => copy(id)}>
                                <LinkIcon fontSize="default"/>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={1}>
                        {
                            connected
                                ?
                                <Tooltip title="Tunnel is online">
                                    <CloudIcon
                                        style={{color: "green"}}/>
                                </Tooltip>
                                :
                                <Tooltip title="Tunnel is offline">
                                    <CloudOffIcon style={{color: "red"}}/>
                                </Tooltip>
                        }
                    </Grid>
                    {
                        description
                            ?
                            <Grid item xs={10}>
                                <Typography variant="body2" color="textSecondary">Description:</Typography>
                                <Typography variant="subtitle1" color="textPrimary">{description}</Typography>
                            </Grid>
                            : null
                    }
                </Grid>
            </Paper>
        </Grid>
    );
}

function renderHubLink(link) {
    switch (link.type) {
        case "GRAFANA": {
            return (
                <Grid container spacing={2} direction={"row"}>
                    <Grid item xs={2}>
                        <img src={GrafanaIcon} alt={link.title} width={20} height={20}/>
                    </Grid>
                    <Grid item xs={4}>
                        {link.title || link.url}
                    </Grid>
                </Grid>
            )
        }
        default: {
            return (link.title || link.url)
        }
    }
}

function renderHubLinks(links) {
    if (!links) return null;

    return (
        <Grid container spacing={2} direction={"column"}>
            {links.map(link => {
                return (
                    <Grid key={`hun_link_${link.id}`} item>
                        <Link href={link.url} target="_blank">
                            {renderHubLink(link)}
                        </Link>
                    </Grid>
                );
            })}
        </Grid>
    );
}

export default function Hub({hubId}) {
    const classes = useStyles();

    const {loading, user} = useAuth0();
    const {handleTitleUpdate} = useNavBarContext();

    if (loading || !user) {
        return <Loading/>;
    }

    const {
        hub,
        hubThings,
        hubLoading,
        actuatorValidationMap,
        handleHubUpdate,
        handleHubThingEnableUpdate,
        handleHubThingCollectionIntervalUpdate,
        handleHubThingActuatorConfigurationChange,
        handleHubThingActuatorConfigurationUpdate,
        handleAddToValidationActuatorMap,
        handleRemoveFromValidationActuatorMap,
        handleHubThingConnectionConfigurationChange,
        handleHubThingConnectionConfigurationUpdate,
        handleHubThingCollectionConfigurationChange,
        handleHubThingCollectionConfigurationUpdate,
        handleHubThingExecuteAction
    } = RagnarokHub(hubId);

    if (hubLoading || !hub) {
        handleTitleUpdate("Loading...");

        return <Loading/>;
    }

    const {title, launched, tunnel, links} = hub;

    handleTitleUpdate(title);

    return (
        <div className={classes.root}>
            <Grid container spacing={2}>
                <Grid item xs={10}>
                    <Grid container spacing={8}>
                        <Grid item>
                            {renderLaunchButton(classes, launched, handleHubUpdate)}
                        </Grid>
                        <Grid item>
                            {renderHubLinks(links)}
                        </Grid>
                    </Grid>
                </Grid>
                {gatewayComponent(classes, tunnel)}
                {
                    renderThings(
                        classes,
                        hubThings,
                        actuatorValidationMap,
                        handleHubThingEnableUpdate,
                        handleHubThingCollectionIntervalUpdate,
                        handleHubThingActuatorConfigurationChange,
                        handleHubThingActuatorConfigurationUpdate,
                        handleAddToValidationActuatorMap,
                        handleRemoveFromValidationActuatorMap,
                        handleHubThingConnectionConfigurationChange,
                        handleHubThingConnectionConfigurationUpdate,
                        handleHubThingCollectionConfigurationChange,
                        handleHubThingCollectionConfigurationUpdate,
                        handleHubThingExecuteAction
                    )
                }
            </Grid>
        </div>
    );
}